import { Typography } from '@analytical-alley/ui';
import { useMemo } from 'react';

import { BaseChart, type EChartsOption, useChart } from '@/components/ECharts';
import { type NumberFormatter, useConfigContext } from '@/context/configContext';

const BAR_COLOR = '#2c55f6';

interface Props {
    title: string | React.ReactNode;
    primary: number;
    comparison: number;
    /** Rotated y-axis title, e.g. "Media Spend". Defaults to `title`. */
    axisLabel: string;
    /** Label for the primary (most recent) bar. */
    primaryLabel?: string;
    /** Label for the comparison (earlier) bar. */
    comparisonLabel?: string;
    /** Overrides the default compact-currency value formatting. */
    formatValue?: (value: number) => string;
}

const deltaColorClass = (delta: number) => {
    if (delta > 0) return 'text-green-400 dark:text-green-400';
    if (delta < 0) return 'text-error dark:text-error';
    return 'text-cyan-300 dark:text-cyan-300';
};

export const ComparisonChartCard = ({
    title,
    primary,
    comparison,
    axisLabel,
    primaryLabel = 'Primary period',
    comparisonLabel = 'Comparison period',
    formatValue,
}: Props) => {
    const { formatNumber } = useConfigContext();

    const format = useMemo<NumberFormatter>(
        () =>
            formatValue
                ? (value) => formatValue(value)
                : (value) =>
                      formatNumber(value),
        [formatValue, formatNumber],
    );

    const delta = primary - comparison;
    const pct = comparison !== 0 ? (delta / comparison) * 100 : 0;

    const deltaText = `${delta >= 0 ? '+' : ''}${format(delta)}`;
    const pctText = `${pct >= 0 ? '+' : ''}${formatNumber(pct, {
        style: 'decimal',
        maximumFractionDigits: 1,
    })}%`;

    const chartOptions = useMemo<EChartsOption>(
        () => ({
            grid: {
                left: 32,
                right: 8,
                top: 16,
                bottom: 0,
                containLabel: true,
            },
            legend: { show: false },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: unknown) => {
                    const [point] = params as { name: string; value: number }[];
                    if (!point) return '';
                    return `${point.name}<br/><strong>${format(point.value)}</strong>`;
                },
            },
            xAxis: [
                {
                    type: 'category',
                    data: [comparisonLabel, primaryLabel],
                    axisTick: { show: false },
                    axisLine: { show: false },
                    axisLabel: {
                        interval: 0,
                        hideOverlap: false,
                        formatter: (value: string) =>
                            value.replace(' – ', '\n'),
                    },
                },
            ],
            yAxis: [
                {
                    type: 'value',
                    axisLine: { show: false },
                    axisLabel: {
                        formatter: (value: number) => format(value),
                    },
                    splitLine: { show: true, lineStyle: { opacity: 0.1 } },
                },
            ],
            series: [
                {
                    name: axisLabel,
                    type: 'bar',
                    color: BAR_COLOR,
                    barMaxWidth: 48,
                    data: [comparison, primary],
                },
            ],
        }),
        [
            axisLabel,
            comparison,
            comparisonLabel,
            format,
            primary,
            primaryLabel,
        ],
    );

    const { ref, chartOptions: finalOptions, onEvents } = useChart({ chartOptions });

    return (
        <div className="glass-tile flex flex-1 flex-col p-8 max-lg:px-5 max-lg:max-w-full">
            <Typography variant="bodyM" className="text-center">
                {title}
            </Typography>
            <Typography
                variant="bodyM"
                className={`text-center font-semibold ${deltaColorClass(delta)}`}
            >
                {deltaText} ({pctText})
            </Typography>
            <div className="h-[200px] w-full">
                <BaseChart option={finalOptions} ref={ref} onEvents={onEvents} $shouldExpand />
            </div>
        </div>
    );
};
